/**
 * PHI-safe projection of a clipboard-write failure for logging.
 *
 * DOMException is matched explicitly, not via `instanceof Error` — its
 * prototype isn't reliably an Error across WebViews, and dropping into the
 * fallback would lose the `name` (NotAllowedError vs other) we log this for.
 * Only DOMException's message is kept (fixed browser strings, no note content);
 * any other error keeps just its name so no user text can leak.
 *
 * Unexpected (non-DOMException) Errors also keep a truncated stack so a
 * serialization regression is locatable instead of surfacing only as a rising
 * `TypeError` count. Only frame lines are kept: a stack's message lines (V8/
 * JSC prefix the stack with the message, which can embed input snippets — a
 * JSON.parse failure quotes the source text) never pair a frame marker with a
 * `:line:col` ending, so the filter drops them even when the message itself
 * spans multiple lines.
 *
 * Kept as a leaf module: the copy telemetry reporter needs it without pulling
 * in the clipboard helpers' dependency graph.
 */
import { RtfClipboardError } from '@/tauri/controller/clipboard';

const MAX_STACK_FRAMES = 8;

// V8 (`    at fn (file:1:2)`) and JSC/Firefox (`fn@file:1:2`) frame lines.
const STACK_FRAME_LINE = /^(?:\s*at\s.+|.+@.+):\d+:\d+\)?$/;

const unexpectedErrorStack = (err: Error): string | undefined => {
  if (typeof err.stack !== 'string') return undefined;
  const frames = err.stack.split('\n').filter((line) => STACK_FRAME_LINE.test(line));
  return frames.slice(0, MAX_STACK_FRAMES).join('\n') || undefined;
};

export const describeClipboardError = (
  err: unknown
): { name?: string; message?: string; stack?: string } => {
  if (err instanceof DOMException) {
    return { name: err.name, message: err.message };
  }
  if (err instanceof RtfClipboardError) {
    return { name: `${err.name}:${err.code}` };
  }
  const name = (err as { name?: unknown } | null)?.name;
  const stack = err instanceof Error ? unexpectedErrorStack(err) : undefined;
  return {
    name: typeof name === 'string' ? name : typeof err,
    ...(stack ? { stack } : {}),
  };
};
